/**
 * FeedbackWidget — Top-level feedback widget rendered as a portal.
 *
 * Orchestrates the entire feedback flow:
 * 1. FAB button (always visible when feature is enabled)
 * 2. Panel (open/close)
 * 3. Recording lifecycle (MediaRecorder + Web Speech API)
 * 4. Comment management
 * 5. LLM aggregation → proposal review → GitHub issue creation
 *
 * Conditionally rendered based on VITE_ENABLE_FEEDBACK_WIDGET env var.
 * Persists sessions to IndexedDB to survive page reloads.
 */
import { useEffect, useCallback, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { toastPatterns } from "../../utils/toastPatterns";

import { useFeedbackStore } from "../../store/feedbackStore";
import { FeedbackFAB } from "./FeedbackFAB";
import { FeedbackPanel } from "./FeedbackPanel";
import { ConfirmDialog } from "./ConfirmDialog";
import { SaveProgressDialog } from "./SaveProgressDialog";
import { ThankYouOverlay } from "./ThankYouOverlay";

import { RecordingService } from "../../services/RecordingService";
import { TranscriptionService } from "../../services/TranscriptionService";
import { captureScreen } from "../../services/ScreenCaptureService";
import { GitHubIssueService } from "../../services/GitHubIssueService";
import { GeminiFeedbackSummaryService } from "../../services/GeminiFeedbackSummaryService";
import { createLogger } from "../../utils/logger";

import type {
  IssueProposal,
  ScreenCapture,
  VoiceComment,
} from "../../types/feedback";

function isFeedbackEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_FEEDBACK_WIDGET === "true";
}

const feedbackLogger = createLogger({ prefix: "FeedbackWidget" });

export function FeedbackWidget() {
  // Feature gate
  if (!isFeedbackEnabled()) return null;

  return <FeedbackWidgetInner />;
}

function FeedbackWidgetInner() {
  const location = useLocation();

  // Store
  const session = useFeedbackStore((s) => s.session);
  const panelOpen = useFeedbackStore((s) => s.panelOpen);
  const isRecording = useFeedbackStore((s) => s.isRecording);
  const interimTranscript = useFeedbackStore((s) => s.interimTranscript);
  const recordingDuration = useFeedbackStore((s) => s.recordingDuration);

  const startSession = useFeedbackStore((s) => s.startSession);
  const endSession = useFeedbackStore((s) => s.endSession);
  const togglePanel = useFeedbackStore((s) => s.togglePanel);
  const setPanelOpen = useFeedbackStore((s) => s.setPanelOpen);
  const addComment = useFeedbackStore((s) => s.addComment);
  const deleteComment = useFeedbackStore((s) => s.deleteComment);
  const setRecording = useFeedbackStore((s) => s.setRecording);
  const setInterimTranscript = useFeedbackStore((s) => s.setInterimTranscript);
  const setRecordingDuration = useFeedbackStore((s) => s.setRecordingDuration);
  const setSessionStatus = useFeedbackStore((s) => s.setSessionStatus);
  const setSessionName = useFeedbackStore((s) => s.setSessionName);
  const setLastSaveResult = useFeedbackStore((s) => s.setLastSaveResult);
  const restoreSession = useFeedbackStore((s) => s.restoreSession);
  const setCommentSummary = useFeedbackStore((s) => s.setCommentSummary);
  const setCommentParaphrase = useFeedbackStore((s) => s.setCommentParaphrase);

  // Local UI state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "end" | "delete";
    commentIndex?: number;
  }>({ open: false, type: "end" });
  const [saveProgress, setSaveProgress] = useState({
    open: false,
    current: 0,
    total: 0,
  });
  const [proposals, setProposals] = useState<IssueProposal[] | null>(null);
  const [isAggregating, setIsAggregating] = useState(false);
  const [thankYou, setThankYou] = useState<{ show: boolean; count: number }>({
    show: false,
    count: 0,
  });
  const isSubmittingRef = useRef(false);

  // Help panel persistence
  const HELP_DISMISSED_KEY = "feedbackHelpDismissed";
  const [helpDismissedPersisted, setHelpDismissedPersisted] = useState(() => {
    try {
      return localStorage.getItem(HELP_DISMISSED_KEY) === "true";
    } catch (error) {
      feedbackLogger.debug(
        "localStorage unavailable for help dismissed state",
        { error }
      );
      return false;
    }
  });

  const handleHelpDismissedPersisted = useCallback((persist: boolean) => {
    setHelpDismissedPersisted(persist);
    try {
      if (persist) {
        localStorage.setItem(HELP_DISMISSED_KEY, "true");
      } else {
        localStorage.removeItem(HELP_DISMISSED_KEY);
      }
    } catch (error) {
      feedbackLogger.debug(
        "localStorage unavailable for help dismissed persist",
        { error }
      );
    }
  }, []);

  // Service refs
  const recordingServiceRef = useRef<RecordingService | null>(null);
  const transcriptionServiceRef = useRef<TranscriptionService | null>(null);
  const summaryServiceRef = useRef(new GeminiFeedbackSummaryService());
  const screenCapturesRef = useRef<ScreenCapture[]>([]);
  const fullTranscriptRef = useRef("");
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Restore session on mount
  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  // Capture screen on route change during active recording
  useEffect(() => {
    if (!isRecording) return;
    const capture = captureScreen();
    screenCapturesRef.current.push(capture);
  }, [location.pathname, isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      recordingServiceRef.current?.abort();
      transcriptionServiceRef.current?.stopLiveTranscription();
    };
  }, []);

  // ------------------------------------------------------------------
  // LLM summary generation (fire-and-forget after each recording)
  // ------------------------------------------------------------------

  const generateSummaryForComment = useCallback(
    (comment: VoiceComment) => {
      void summaryServiceRef.current
        .generateCommentSummaryAndParaphrase(comment)
        .then(({ summary, paraphrase }) => {
          setCommentSummary(comment.index, summary);
          setCommentParaphrase(comment.index, paraphrase);
        });
    },
    [setCommentSummary, setCommentParaphrase]
  );

  // ------------------------------------------------------------------
  // Recording lifecycle
  // ------------------------------------------------------------------

  const startRecording = useCallback(async () => {
    try {
      // Initialize services
      const recorder = new RecordingService();
      recordingServiceRef.current = recorder;
      screenCapturesRef.current = [];
      fullTranscriptRef.current = "";

      // Capture initial screen
      const initialCapture = captureScreen();
      screenCapturesRef.current.push(initialCapture);

      // Start recording
      await recorder.startRecording((seconds) => setRecordingDuration(seconds));
      setRecording(true);
      setRecordingDuration(0);
      setInterimTranscript("");

      // Duration counter
      let elapsed = 0;
      durationIntervalRef.current = setInterval(() => {
        elapsed++;
        setRecordingDuration(elapsed);
      }, 1000);

      // Start live transcription
      const transcriber = new TranscriptionService();
      transcriptionServiceRef.current = transcriber;

      transcriber.startLiveTranscription({
        onInterim: (text) => setInterimTranscript(text),
        onFinal: (text) => {
          fullTranscriptRef.current += text;
          setInterimTranscript("");
        },
        onError: (_error) => {
          // Transcription error is non-fatal — recording continues
        },
      });
    } catch (error) {
      setRecording(false);
      toastPatterns.error(
        "Unable to start recording. Please allow microphone access."
      );
      feedbackLogger.warn("Failed to start recording", { error });
    }
  }, [setRecording, setRecordingDuration, setInterimTranscript]);

  const stopRecording = useCallback(async () => {
    // Stop duration counter
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Stop transcription
    transcriptionServiceRef.current?.stopLiveTranscription();
    transcriptionServiceRef.current = null;

    // Stop recording and get audio
    const recorder = recordingServiceRef.current;
    if (!recorder) {
      setRecording(false);
      return;
    }

    try {
      const result = await recorder.stopRecording();
      recordingServiceRef.current = null;

      // Convert blob to data URL for IndexedDB persistence
      let audioDataUrl: string | null = null;
      if (result.blob.size > 0) {
        audioDataUrl = await blobToDataUrl(result.blob);
      }

      const transcription = fullTranscriptRef.current.trim();

      // Discard empty recordings
      if (!audioDataUrl && transcription.length === 0) {
        toastPatterns.info("Empty recording discarded.");
        setRecording(false);
        setInterimTranscript("");
        setRecordingDuration(0);
        return;
      }

      if (transcription.length === 0) {
        toastPatterns.error(
          "Transcription failed. Please try recording again."
        );
        setRecording(false);
        setInterimTranscript("");
        setRecordingDuration(0);
        return;
      }

      const commentIndex = (session?.comments.length ?? 0) + 1;
      const uniqueScreenCaptures = dedupeCapturesByRoute(
        screenCapturesRef.current
      );
      const pageTitle =
        uniqueScreenCaptures[0]?.pageTitle?.trim() || document.title || "Page";

      const comment: VoiceComment = {
        index: commentIndex,
        audioDataUrl,
        audioMimeType: result.mimeType,
        durationSeconds: result.durationSeconds,
        transcription,
        title: `${pageTitle} ${commentIndex}`,
        summary: null,
        paraphrase: null,
        screenCaptures: uniqueScreenCaptures,
        transcriptionStatus: "complete",
        recordedAt: new Date().toISOString(),
      };

      addComment(comment);

      // Fire-and-forget LLM summary generation
      generateSummaryForComment(comment);
    } catch (error) {
      feedbackLogger.warn("Recording stop failed", { error });
    }

    setRecording(false);
    setInterimTranscript("");
    setRecordingDuration(0);
  }, [
    session,
    addComment,
    setRecording,
    setInterimTranscript,
    setRecordingDuration,
    generateSummaryForComment,
  ]);

  const handleToggleRecording = useCallback(async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // ------------------------------------------------------------------
  // Text comment handler
  // ------------------------------------------------------------------

  const handleAddTextComment = useCallback(
    (text: string) => {
      const commentIndex = (session?.comments.length ?? 0) + 1;
      const capture = captureScreen();
      const pageTitle = capture.pageTitle?.trim() || document.title || "Page";

      const comment: VoiceComment = {
        index: commentIndex,
        audioDataUrl: null,
        audioMimeType: "",
        durationSeconds: 0,
        transcription: text,
        title: `${pageTitle} ${commentIndex}`,
        summary: null,
        paraphrase: null,
        screenCaptures: [capture],
        transcriptionStatus: "complete",
        recordedAt: new Date().toISOString(),
      };

      addComment(comment);
      generateSummaryForComment(comment);
    },
    [session, addComment, generateSummaryForComment]
  );

  // ------------------------------------------------------------------
  // FAB click
  // ------------------------------------------------------------------

  const handleFABClick = useCallback(() => {
    if (session) {
      togglePanel();
    } else {
      startSession();
    }
  }, [session, togglePanel, startSession]);

  // ------------------------------------------------------------------
  // Save flow: aggregate → review → confirm → create
  // ------------------------------------------------------------------

  /** Step 1: Aggregate comments into proposals via LLM */
  const handleSave = useCallback(async () => {
    if (!session || session.comments.length === 0) return;

    setIsAggregating(true);
    try {
      const service = summaryServiceRef.current;
      const [aggregated, nextSessionTitle] = await Promise.all([
        service.aggregateSessionIssues(session),
        service.generateSessionTitle(session),
      ]);

      if (nextSessionTitle) {
        setSessionName(nextSessionTitle);
      }

      setProposals(aggregated);
    } catch (error) {
      toastPatterns.error("Failed to analyze feedback. Please try again.");
      feedbackLogger.warn("LLM aggregation failed", { error });
    } finally {
      setIsAggregating(false);
    }
  }, [session, setSessionName]);

  /** Step 2: User edits a proposal */
  const handleUpdateProposal = useCallback(
    (index: number, updated: IssueProposal) => {
      setProposals((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        next[index] = updated;
        return next;
      });
    },
    []
  );

  /** Step 2: User deletes a proposal */
  const handleDeleteProposal = useCallback((index: number) => {
    setProposals((prev) => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  /** Step 3: Confirm — create GitHub issues from proposals */
  const handleConfirmProposals = useCallback(async () => {
    if (!session || !proposals || proposals.length === 0) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setSessionStatus("saving");
      setSaveProgress({ open: true, current: 0, total: proposals.length });

      const service = new GitHubIssueService();
      const result = await service.saveProposals(
        proposals,
        session,
        (current, total) => {
          setSaveProgress({ open: true, current, total });
        }
      );

      setSaveProgress({ open: false, current: 0, total: 0 });
      setLastSaveResult(result);
      setProposals(null);

      if (result.issuesCreated === proposals.length) {
        setSessionStatus("saved");
        toastPatterns.success(`Feedback submitted — thank you!`);
        setThankYou({ show: true, count: result.issuesCreated });
      } else {
        setSessionStatus("error");
        toastPatterns.error(
          `Something went wrong submitting feedback. Please try again.`
        );
      }
    } finally {
      isSubmittingRef.current = false;
    }
  }, [session, proposals, setSessionStatus, setLastSaveResult]);

  /** Cancel — go back to recording mode */
  const handleCancelProposals = useCallback(() => {
    setProposals(null);
  }, []);

  /** Dismiss the thank you overlay and end the session */
  const handleThankYouDismiss = useCallback(() => {
    setThankYou({ show: false, count: 0 });
    endSession();
  }, [endSession]);

  // ------------------------------------------------------------------
  // Confirm dialogs
  // ------------------------------------------------------------------

  const handleEndSession = useCallback(() => {
    setConfirmDialog({ open: true, type: "end" });
  }, []);

  const handleDeleteComment = useCallback((index: number) => {
    setConfirmDialog({ open: true, type: "delete", commentIndex: index });
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmDialog.type === "end") {
      if (isRecording) {
        recordingServiceRef.current?.abort();
        transcriptionServiceRef.current?.stopLiveTranscription();
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }
        setRecording(false);
      }
      setProposals(null);
      endSession();
    } else if (
      confirmDialog.type === "delete" &&
      confirmDialog.commentIndex !== undefined
    ) {
      deleteComment(confirmDialog.commentIndex);
    }
    setConfirmDialog({ open: false, type: "end" });
  }, [confirmDialog, isRecording, endSession, deleteComment, setRecording]);

  const handleCancelConfirm = useCallback(() => {
    setConfirmDialog({ open: false, type: "end" });
  }, []);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  const isSaving = session?.status === "saving";

  const content = (
    <div id="feedback-widget" className="fb-widget">
      <FeedbackFAB
        hasSession={!!session}
        isRecording={isRecording}
        commentCount={session?.comments.length ?? 0}
        onClick={handleFABClick}
      />

      {session && panelOpen && (
        <FeedbackPanel
          session={session}
          isRecording={isRecording}
          recordingDuration={recordingDuration}
          interimTranscript={interimTranscript}
          isSaving={!!isSaving}
          isAggregating={isAggregating}
          proposals={proposals}
          helpDismissedPersisted={helpDismissedPersisted}
          onClose={() => setPanelOpen(false)}
          onToggleRecording={() => void handleToggleRecording()}
          onSave={() => void handleSave()}
          onEndSession={handleEndSession}
          onDeleteComment={handleDeleteComment}
          onRenameSession={setSessionName}
          onUpdateProposal={handleUpdateProposal}
          onDeleteProposal={handleDeleteProposal}
          onConfirmProposals={() => void handleConfirmProposals()}
          onCancelProposals={handleCancelProposals}
          onAddTextComment={handleAddTextComment}
          onHelpDismissedPersisted={handleHelpDismissedPersisted}
        />
      )}

      {/* Thank you overlay */}
      {thankYou.show && (
        <ThankYouOverlay
          issueCount={thankYou.count}
          onDismiss={handleThankYouDismiss}
        />
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.type === "end" ? "End Session?" : "Delete Comment?"
        }
        message={
          confirmDialog.type === "end"
            ? "This will discard all unsaved comments. This action cannot be undone."
            : `Delete comment #${confirmDialog.commentIndex ?? ""}?`
        }
        confirmLabel={confirmDialog.type === "end" ? "End Session" : "Delete"}
        variant="danger"
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />

      {/* Save progress */}
      <SaveProgressDialog
        open={saveProgress.open}
        current={saveProgress.current}
        total={saveProgress.total}
        sessionName={session?.sessionName ?? ""}
      />
    </div>
  );

  return createPortal(content, document.body);
}

// ------------------------------------------------------------------
// Utility
// ------------------------------------------------------------------

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function dedupeCapturesByRoute(captures: ScreenCapture[]): ScreenCapture[] {
  const seenRoutes = new Set<string>();
  const deduped: ScreenCapture[] = [];

  for (const capture of captures) {
    if (seenRoutes.has(capture.route)) {
      continue;
    }
    seenRoutes.add(capture.route);
    deduped.push(capture);
  }

  return deduped;
}
